import { useEffect, useMemo, useRef, useState } from "react";
import { Search, CornerDownLeft } from "lucide-react";
import { api, type SearchGroup } from "../lib/api.js";
import type { Route, RouteTarget } from "../lib/useRoute.js";

const PAGES: { route: Route; label: string }[] = [
  { route: "overview", label: "Overview" },
  { route: "tasks", label: "Tasks" },
  { route: "sources", label: "Sources" },
  { route: "research", label: "Keyword Research" },
  { route: "pipeline", label: "Pipeline" },
  { route: "content", label: "Articles" },
  { route: "content-db", label: "Content database" },
  { route: "entities", label: "Entities" },
  { route: "calendar", label: "Calendar" },
  { route: "video", label: "Video" },
  { route: "distribution", label: "Distribution" },
  { route: "audience", label: "Audience" },
  { route: "audit", label: "Site Audit" },
  { route: "credentials", label: "Credentials" },
  { route: "errors", label: "Errors" },
  { route: "spend", label: "Spend" },
  { route: "brand", label: "Brand kit" },
  { route: "help", label: "Help center" },
];

interface Entry {
  key: string;
  title: string;
  subtitle?: string;
  group: string;
  route: Route;
}

export function CommandPalette({
  open,
  onClose,
  onNavigate,
}: {
  open: boolean;
  onClose: () => void;
  onNavigate: (r: RouteTarget) => void;
}) {
  const [q, setQ] = useState("");
  const [groups, setGroups] = useState<SearchGroup[]>([]);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset + focus each time it opens.
  useEffect(() => {
    if (open) {
      setQ("");
      setGroups([]);
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Debounced server search.
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) {
      setGroups([]);
      return;
    }
    const t = setTimeout(() => {
      api
        .search(term)
        .then((r) => setGroups(r.groups))
        .catch(() => setGroups([]));
    }, 180);
    return () => clearTimeout(t);
  }, [q, open]);

  const entries = useMemo<Entry[]>(() => {
    const pageMatches = PAGES.filter(
      (p) => !q.trim() || p.label.toLowerCase().includes(q.trim().toLowerCase()),
    ).map((p) => ({ key: `page-${p.route}`, title: p.label, group: "Go to", route: p.route }));

    const results = groups.flatMap((g) =>
      g.items.map((it) => ({
        key: `${g.type}-${it.id}`,
        title: it.title,
        subtitle: it.subtitle,
        group: g.label,
        route: g.route as Route,
      })),
    );
    return [...results, ...pageMatches];
  }, [groups, q]);

  useEffect(() => {
    setActive((a) => Math.min(a, Math.max(entries.length - 1, 0)));
  }, [entries.length]);

  if (!open) return null;

  const choose = (e?: Entry) => {
    const target = e ?? entries[active];
    if (!target) return;
    onNavigate(target.route);
    onClose();
  };

  // Render grouped, but keep a flat index for keyboard nav.
  let flatIndex = -1;
  const grouped = entries.reduce<Record<string, Entry[]>>((acc, e) => {
    (acc[e.group] ??= []).push(e);
    return acc;
  }, {});

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-ink/20 p-4 pt-[12vh] backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-xl border border-line bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-line px-4">
          <Search className="h-4 w-4 shrink-0 text-muted" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActive((a) => Math.min(a + 1, entries.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActive((a) => Math.max(a - 1, 0));
              } else if (e.key === "Enter") {
                e.preventDefault();
                choose();
              } else if (e.key === "Escape") {
                onClose();
              }
            }}
            placeholder="Search keywords, articles, clusters, entities… or jump to a page"
            className="w-full bg-transparent py-3.5 text-sm text-ink placeholder:text-muted focus:outline-none"
          />
        </div>

        <div className="max-h-[52vh] overflow-y-auto py-2">
          {entries.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-muted">
              {q.trim().length < 2 ? "Type to search…" : "No matches."}
            </p>
          ) : (
            Object.entries(grouped).map(([group, list]) => (
              <div key={group} className="mb-1">
                <div className="px-4 py-1 text-2xs font-semibold uppercase tracking-wider text-muted">
                  {group}
                </div>
                {list.map((e) => {
                  flatIndex += 1;
                  const idx = flatIndex;
                  return (
                    <button
                      key={e.key}
                      onMouseEnter={() => setActive(idx)}
                      onClick={() => choose(e)}
                      className={`flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm ${
                        idx === active ? "bg-brand-50 text-brand-900" : "text-ink hover:bg-line/40"
                      }`}
                    >
                      <span className="min-w-0 flex-1 truncate">
                        {e.title}
                        {e.subtitle && (
                          <span className="ml-2 text-xs text-muted">{e.subtitle}</span>
                        )}
                      </span>
                      {idx === active && <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-muted" />}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
